/**
 * Refund settlement, shared by the invoice UI and documented for
 * private.settle_invoice_refund in
 * supabase/migrations/20261103260000_refund_invoice_credit_note.sql.
 *
 * There is no partially_refunded invoice status. A partial refund writes a
 * credit note for the part of the bill that was given back, and a negative
 * payment on the refund date so kasseafstemning drops that cash on that day.
 * The invoice stays paid when the money still collected covers the bill after
 * the credit note. It becomes refunded only when the credit note covers the
 * bill and nothing remains collected.
 */

export type RefundInvoiceStatus = 'refunded' | 'paid' | 'partial' | 'issued' | 'overdue';

export function creditCentsForRefund(input: {
  invoiceTotalCents: number;
  alreadyCreditedCents: number;
  collectedCents: number;
  refundCents: number;
}): number {
  const obligation = Math.max(0, input.invoiceTotalCents - input.alreadyCreditedCents);
  const surplus = Math.max(0, input.collectedCents - obligation);
  const fromBill = input.refundCents - surplus;
  return Math.max(0, Math.min(fromBill, obligation));
}

export function statusAfterRefund(input: {
  invoiceTotalCents: number;
  creditedCents: number;
  netCollectedCents: number;
  dueAt: string | null;
  today: string;
}): RefundInvoiceStatus {
  const obligation = input.invoiceTotalCents - input.creditedCents;
  if (obligation <= 0 && input.netCollectedCents <= 0) return 'refunded';
  if (input.netCollectedCents <= 0) {
    if (input.dueAt != null && input.dueAt < input.today) return 'overdue';
    return 'issued';
  }
  if (input.netCollectedCents >= obligation) return 'paid';
  return 'partial';
}

const NO_BALANCE = new Set(['refunded', 'void', 'cancelled', 'credited']);

/** What the patient still owes. A refunded invoice owes nothing, even though total_paid is 0. */
export function invoiceBalanceDueCents(input: {
  status: string;
  totalCents: number;
  paidCents: number;
  creditedCents?: number;
}): number {
  if (NO_BALANCE.has(input.status)) return 0;
  const obligation = Math.max(0, input.totalCents - (input.creditedCents ?? 0));
  return Math.max(0, obligation - Math.max(0, input.paidCents));
}

const LEDGER_METHODS = new Set([
  'cash',
  'card',
  'bank_transfer',
  'mobilepay',
  'other',
  'kontant',
  'kort',
]);

/** Cash, card and manual MobilePay refund on the ledger. Clinio Pay stays on the provider. */
export function refundUsesLedger(input: {
  payToken: string | null | undefined;
  paymentMethod: string | null | undefined;
  provider: string | null | undefined;
}): boolean {
  const provider = (input.provider ?? '').toLowerCase();
  if (provider === 'clinio_pay' || provider === 'stripe') return false;
  const method = (input.paymentMethod ?? '').trim().toLowerCase();
  if (method === 'clinio_pay') return false;
  if (LEDGER_METHODS.has(method)) return true;
  return !input.payToken;
}
